import {
  AgGrid,
  ErrorMessage,
  IconDropdown,
  MaterialIcon,
  Spinner,
  MobileResponsiveGrid,
  InfoPopup,
} from "@/common/components";
import { useContext, useEffect, useState } from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import { useProductDetailsQuery } from "@/service/ProductService";
import { resetOwnersState } from "@/store/ownersSlice";
import { setProductDetails } from "@/store/productSlice";
import { Constants } from "@/utils/Constants";
import { addPercentage } from "@/utils/Utilities";

const ProductList = () => {
  const {
    labels,
    navigate,
    setSubNavHeader,
    setSubNavButtonText,
    setSubNavButtonClick,
    productSponsorId,
    cloneDeep,
    Routes,
    accessProfileTypeCode,
    isRiccAdministrator,
    accessorPrivileges,
    dispatch,
    setNavCancelMessage,
  } = useContext(PrivateContext);

  const [rowData, setRowData] = useState([]);
  const [selectedProductData, setSelectedProductData] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [sortOption, setSortOption] = useState("Name (A to Z)");

  const {
    data: productDetails,
    isFetching,
    error,
  } = useProductDetailsQuery({
    productSponsorId: productSponsorId,
  });

  const actionsDropdown = (params) => {
    const productData = params?.data;
    const contractAndCertificate = params.data.productOptionSettings?.find(
      (e) => e.type === "CONTRACT_CERTIFICATE_NUMBER_PROCESSING_REQUIRED"
    );
    return (
      <IconDropdown
        {...params}
        list={[
          {
            text: labels?.productDetail.viewProductDetails,
            path: Routes.viewProductDetails.path?.replace(":id", 1),
          },
          {
            text: labels?.productDetail.investmentVehicles,
            path: Routes.investmentVehiclesProduct.path,
          },
          {
            text: labels?.productDetail.insuranceCompanies,
            path: Routes.insuranceCompaniesProduct.path,
          },
          // Purchase Table
          {
            text: labels?.productDetail.purchaseTables,
            path: Routes?.purchaseTables?.path,
            hide: !(
              productData.productType === "F" &&
              productData.insuranceCompanySetupComplete === "true" &&
              productData.investmentVehiclesSetupComplete === "true"
            ),
          },
          // Payment Table
          {
            text: labels?.productDetail.paymentTable,
            path: Routes.paymentTable.path,
            hide: productData?.insuranceCompanySetupComplete !== "true",
          },
          {
            text: labels?.productDetail.transactionMatrix,
            path: Routes?.transactionMatrix?.path,
          },
          // Product Rules
          {
            text: labels?.productDetail.productRules,
            path: Routes.productRules.path,
            hide: accessProfileTypeCode === "I",
          },
          // Contract & Certificate
          {
            text: labels?.productDetail?.contractCertificate,
            path: Routes?.contractCertificate?.path,
            hide: !(
              contractAndCertificate && contractAndCertificate?.value === "Y"
            ),
          },
          {
            text: labels?.productDetail.duplicateProduct,
            click: () => {
              if (!productData?.setupCompleteDt) {
                setSelectedProductData(productData);
                setShowPopup(true);
              } else {
                navigate(Routes?.duplicateProduct?.path?.replace(":id", 1), {
                  state: productData,
                });
              }
            },
            hide:
              !isRiccAdministrator &&
              !(
                accessorPrivileges &&
                (accessorPrivileges["PRODUCT"] === "ADD" ||
                  accessorPrivileges["PRODUCT"] === "UPDATE")
              ),
          },
        ]}
      />
    );
  };

  const transformToMobileColDefs = (columnDefs) => {
    return columnDefs.map((colDef) => {
      if (colDef.field === "description") {
        const newColDef = { ...colDef };
        delete newColDef.tooltipField;
        delete newColDef.cellRenderer;
        delete newColDef.tooltipComponentParams;

        return {
          ...newColDef,
          clickToExpand: true,
          truncateLength: 15,
        };
      }
      return colDef;
    });
  };

  const sortedEntities = [...rowData]?.sort((a, b) => {
    if (sortOption === "Name (A to Z)") {
      return a?.name.localeCompare(b?.name);
    } else if (sortOption === "Name (Z to A)") {
      return b?.name.localeCompare(a?.name);
    }
    return 0;
  });

  const columnDefs = [
    {
      field: "name",
      headerName: labels?.productDetail.name,
      flex: 2,
      sortable: true,
    },
    {
      headerName: labels?.productDetail?.actions,
      colId: "actions",
      cellRenderer: actionsDropdown,
      sortable: false,
    },
    {
      field: "status",
      headerName: labels?.productDetail.status,
      cellRenderer: getFormattedStatus,
      flex: 1.2,
    },
    {
      field: "setupComplete",
      headerName: labels?.productDetail.setUp,
      cellRenderer: getSetupValue,
      flex: 1.2,
    },
    {
      field: "externalId",
      headerName: labels?.productDetail.externalId,
      flex: 1.5,
      sortable: true,
    },
    {
      field: "productType",
      cellRenderer: getFormattedProductType,
      headerName: labels?.productDetail.type,
      flex: 1.2,
      sortable: true,
    },
    {
      field: "description",
      headerName: labels?.productDetail.description,
      flex: 1.6,
      tooltipField: "description",
      cellRenderer: (params) => {
        const value = params.value || "";
        const truncated =
          value.length > 15 ? value.slice(0, 15) + "..." : value;
        return `${truncated}`;
      },
      tooltipComponentParams: {
        tooltipTimeout: 500,
      },
      sortable: true,
    },
    {
      field: "numberOfProductInvestmentVehicles",
      headerName: labels?.productDetail.investmentVehicles,
      flex: 1.5,
      sortable: true,
    },
    {
      field: "insuranceCoverageRate",
      headerName: labels?.productDetail.insuranceCoverageRate,
      flex: 1.6,
      cellRenderer: addPercentage,
      sortable: true,
    },
  ];

  const mobileColumnDefs = transformToMobileColDefs(columnDefs);

  function getFormattedProductType(params) {
    if (params.value === "F") {
      return "Fixed";
    } else if (params.value === "W") {
      return "GMWB";
    } else if (params.value === "I") {
      return "GMIB";
    }
  }

  function getSetupValue(params) {
    if (params.value === "true") {
      return (
        <MaterialIcon
          wrapperClassName="ms-3"
          iconName="check_circle"
          iconClassName="check_circle"
        />
      );
    }
    return "";
  }

  function getFormattedStatus(params) {
    if (params.value != undefined) {
      return params.value.charAt(0).toUpperCase() + params.value.slice(1);
    }
  }

  function initialSortProduct(products) {
    products.sort((a, b) => {
      const statusComparison = compareStatus(a.status, b.status);
      if (statusComparison !== 0) {
        return statusComparison;
      }
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
    return products;
  }

  function compareStatus(statusA, statusB) {
    return getStatusOrder(statusA) - getStatusOrder(statusB);
  }

  function getStatusOrder(status) {
    if (status === Constants.PRODUCT_STATUS_ACTIVE) {
      return 0;
    } else if (status === Constants.PRODUCT_STATUS_INACTIVE) {
      return 1;
    } else if (status === Constants.PRODUCT_STATUS_CLOSED) {
      return 2;
    } else {
      return 3;
    }
  }

  const addNewProduct = () => {
    navigate(Routes.addNewProduct.path?.replace(":id", 1));
  };

  const navigateToDuplicateProducts = () => {
    navigate(Routes?.duplicateProduct?.path?.replace(":id", 1), {
      state: selectedProductData,
    });
  };

  useEffect(() => {
    dispatch(resetOwnersState());
    if (productDetails) {
      let sortedData = initialSortProduct(cloneDeep(productDetails?.products));
      sortedData = sortedData.map((e) => {
        e.productOptionSettings.forEach((e1) => {
          e[e1.type] = e1.value;
        });
        return e;
      });
      setRowData(sortedData);
    }
  }, [productDetails]);

  useEffect(() => {
    setSubNavHeader(labels?.productDetail?.navHeader);
    setSubNavButtonText(
      (accessorPrivileges &&
        (accessorPrivileges["PRODUCT"] === "ADD" ||
          accessorPrivileges["PRODUCT"] === "UPDATE")) ||
        isRiccAdministrator
        ? labels?.productDetail?.addProduct
        : ""
    );
    setSubNavButtonClick(() => addNewProduct);
    dispatch(setProductDetails({}));
    setNavCancelMessage("");
  }, [accessorPrivileges]);

  return (
    <section className="py-5">
      {isFetching && <Spinner />}
      <ErrorMessage error={error} wrapperClassName="mb-3" />
      <AgGrid
        rowData={rowData}
        columnDefs={columnDefs}
        wrapperClassNames="productListGrid mb-5 d-md-block d-none"
        domLayout="autoHeight"
      />
      <MobileResponsiveGrid
        rowData={sortedEntities}
        columnDefs={mobileColumnDefs}
        showSortDropDown={true}
        sortOption={sortOption}
        setSortOption={setSortOption}
      />
      <InfoPopup
        showModal={showPopup}
        primaryButtonText={labels?.productDetail.continue}
        primaryButtonClick={navigateToDuplicateProducts}
        secondaryButtonText={labels?.productDetail.cancel}
        secondaryButtonClick={() => setShowPopup(false)}
        body={labels?.productDetail.duplicationMsg}
      />
    </section>
  );
};

export default ProductList;
